/**
 * Aggregation helpers for the config-driven dashboard.
 * Mirrors the behaviour of dashboard_builder.py (sum/count/mean/min/max/groupBy/groupByTwo).
 */

export function aggSum(data, field) {
  return data.reduce((s, d) => s + (parseFloat(d[field]) || 0), 0)
}
export function aggCount(data) { return data.length }
export function aggMean(data, field) {
  return data.length ? aggSum(data, field) / data.length : 0
}
export function aggMin(data, field) {
  if (!data.length) return 0
  let min = Infinity
  for (const d of data) {
    const v = parseFloat(d[field]) || 0
    if (v < min) min = v
  }
  return min === Infinity ? 0 : min
}
export function aggMax(data, field) {
  if (!data.length) return 0
  let max = -Infinity
  for (const d of data) {
    const v = parseFloat(d[field]) || 0
    if (v > max) max = v
  }
  return max === -Infinity ? 0 : max
}

export function aggregate(data, field, aggType) {
  const t = (aggType || 'sum').toLowerCase()
  if (t === 'count') return aggCount(data)
  if (t === 'mean' || t === 'avg') return aggMean(data, field)
  if (t === 'min') return aggMin(data, field)
  if (t === 'max') return aggMax(data, field)
  return aggSum(data, field)
}

export function groupBy(data, key, valField, aggType) {
  const map = Object.create(null)
  const cnt = Object.create(null)
  for (const d of data) {
    const k = d[key] != null ? d[key] : 'Unknown'
    const v = parseFloat(d[valField]) || 0
    if (!(k in map)) { map[k] = 0; cnt[k] = 0 }
    map[k] += v
    cnt[k]++
  }
  const t = (aggType || 'sum').toLowerCase()
  if (t === 'count') {
    for (const k of Object.keys(map)) map[k] = cnt[k]
  } else if (t === 'mean' || t === 'avg') {
    for (const k of Object.keys(map)) map[k] = cnt[k] ? map[k] / cnt[k] : 0
  } else if (t === 'min') {
    const minMap = Object.create(null)
    for (const d of data) {
      const k = d[key] != null ? d[key] : 'Unknown'
      const v = parseFloat(d[valField]) || 0
      if (!(k in minMap) || v < minMap[k]) minMap[k] = v
    }
    return minMap
  } else if (t === 'max') {
    const maxMap = Object.create(null)
    for (const d of data) {
      const k = d[key] != null ? d[key] : 'Unknown'
      const v = parseFloat(d[valField]) || 0
      if (!(k in maxMap) || v > maxMap[k]) maxMap[k] = v
    }
    return maxMap
  }
  return map
}

export function groupByTwo(data, xField, seriesField, valField, aggType) {
  const result = Object.create(null)
  const cnt = Object.create(null)
  for (const d of data) {
    const s = d[seriesField] != null ? d[seriesField] : 'Unknown'
    const x = d[xField] != null ? d[xField] : 'Unknown'
    const v = parseFloat(d[valField]) || 0
    if (!result[s]) { result[s] = Object.create(null); cnt[s] = Object.create(null) }
    if (!(x in result[s])) { result[s][x] = 0; cnt[s][x] = 0 }
    result[s][x] += v
    cnt[s][x]++
  }
  const t = (aggType || 'sum').toLowerCase()
  if (t === 'count') return cnt
  if (t === 'mean' || t === 'avg') {
    for (const s of Object.keys(result)) {
      for (const x of Object.keys(result[s])) {
        result[s][x] = cnt[s][x] ? result[s][x] / cnt[s][x] : 0
      }
    }
  }
  return result
}

export function sortGrouped(map, order, limit) {
  let entries = Object.entries(map)
  if (order === 'asc')       entries.sort((a, b) => a[1] - b[1])
  else if (order === 'desc') entries.sort((a, b) => b[1] - a[1])
  if (limit && limit > 0)    entries = entries.slice(0, limit)
  const out = Object.create(null)
  for (const [k, v] of entries) out[k] = v
  return out
}

export function isDateField(data, field) {
  let hits = 0, checks = 0
  for (const d of data) {
    if (d[field] == null) continue
    if (!isNaN(Date.parse(String(d[field]))) && String(d[field]).length >= 8) hits++
    checks++
    if (checks >= 5) break
  }
  return hits >= 3
}

export function sortByDate(labels, values) {
  const pairs = labels.map((l, i) => [l, values[i]])
  pairs.sort((a, b) => new Date(a[0]) - new Date(b[0]))
  return { labels: pairs.map(p => p[0]), values: pairs.map(p => p[1]) }
}

export function uniqueValues(data, field) {
  const s = new Set()
  for (const d of data) {
    const v = d[field]
    if (v != null) s.add(v)
  }
  return [...s]
}

export function computeCustomColumn(row, formula) {
  if (!formula || !formula.type) return ''
  const a = formula.a, b = formula.b
  const va = a != null ? Number(row[a]) : NaN
  const vb = b != null ? Number(row[b]) : NaN
  const round2 = n => (isFinite(n) ? (Math.round(n * 100) / 100) : '')
  switch (formula.type) {
    case 'sum':        return round2((isFinite(va) ? va : 0) + (isFinite(vb) ? vb : 0))
    case 'difference': return round2((isFinite(va) ? va : 0) - (isFinite(vb) ? vb : 0))
    case 'product':    return round2((isFinite(va) ? va : 0) * (isFinite(vb) ? vb : 0))
    case 'ratio':      return (isFinite(va) && isFinite(vb) && vb !== 0) ? round2(va / vb) : ''
    case 'percent':    return (isFinite(va) && isFinite(vb) && vb !== 0)
      ? (round2((va / vb) * 100) + '%') : ''
    case 'concat': {
      const sep = formula.separator != null ? formula.separator : ' '
      const parts = [row[a], row[b]].filter(x => x != null && x !== '')
      return parts.join(sep)
    }
    case 'constant':   return formula.value != null ? formula.value : ''
    default:           return ''
  }
}
